import { db } from '../firebase';
import { doc, setDoc, serverTimestamp, collection, getDocs, deleteField } from 'firebase/firestore';

/**
 * Saves travel plan enquiry data to Firestore
 * Uses a structure of enquiries(collection) > Month,Year(document) > entries (map field)
 * @param {Object} formData - Travel plan form data
 * @returns {Promise} - Promise representing the save operation
 */
export const saveEnquiry = async (formData) => {
  try {
    // Get current date for document ID
    const now = new Date();
    const month = now.toLocaleString('default', { month: 'long' });
    const year = now.getFullYear();
    const documentId = `${month},${year}`;
    
    // Generate unique ID for this enquiry
    const enquiryId = `enquiry_${now.getTime()}`;
    
    // Create reference to the month/year document
    const monthYearDocRef = doc(db, 'enquiries', documentId);
    
    // Extract contact info and organize data into nested structure
    const { contactInfo, ...tripDetails } = formData;
    
    // Structure data with nested maps - contactInfo and details
    // Keep timestamp directly inside the enquiry entry and remove createdAt
    const dataToSave = {
      contactInfo: {
        ...contactInfo
      },
      details: {
        ...tripDetails
      },
      timestamp: serverTimestamp(),
    };
    
    // Create a map structure with the enquiry ID as the key
    // Using dot notation for Firestore map field updates
    const enquiryData = {
      [`entries.${enquiryId}`]: dataToSave
    };
    
    // Save the data to Firestore, merging with any existing data
    await setDoc(monthYearDocRef, enquiryData, { merge: true });
    
    return {
      success: true,
      enquiryId,
      message: 'Enquiry saved successfully',
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: 'Failed to save enquiry',
    };
  }
};

/**
 * Fetches all enquiries from Firestore
 * @returns {Promise<Array>} - Promise resolving to array of enquiry objects
 */
export const fetchAllEnquiries = async () => {
  try {
    // Get reference to the enquiries collection
    const enquiriesCollectionRef = collection(db, 'enquiries');
    
    // Get all documents in the collection
    const querySnapshot = await getDocs(enquiriesCollectionRef);
    
    // Array to store all enquiries
    const allEnquiries = [];
    
    // Loop through each document (month,year)
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Process fields that begin with "entries."
      Object.keys(data).forEach(key => {
        if (key.startsWith('entries.')) {
          const entryId = key.split('.')[1]; // Get the ID part
          const entryData = data[key];
          
          // Format each entry for display
          allEnquiries.push({
            id: entryId,
            date: entryData.timestamp ? new Date(entryData.timestamp.toDate()).toLocaleDateString() : 'N/A',
            name: entryData.contactInfo?.name || 'Unknown',
            email: entryData.contactInfo?.email || 'Unknown',
            subject: entryData.details?.destination || entryData.details?.destinations?.[0] || 'General Inquiry',
            status: entryData.status || 'New',
            contactInfo: entryData.contactInfo,  // This includes the personCount
            details: entryData.details,
            rawData: entryData
          });
        }
      });
    });
    
    // Sort enquiries by date (newest first)
    allEnquiries.sort((a, b) => {
      if (a.date === 'N/A' || b.date === 'N/A') return 0;
      return new Date(b.date) - new Date(a.date);
    });
    
    return {
      success: true,
      data: allEnquiries,
      message: 'Enquiries fetched successfully',
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      error: error.message,
      message: 'Failed to fetch enquiries',
    };
  }
};

/**
 * Updates the status of an inquiry in Firestore
 * @param {string} inquiryId - The ID of the inquiry
 * @param {string} newStatus - The new status to set
 * @param {string} type - The type of inquiry ('booking' or general inquiry)
 * @returns {Promise} - Promise representing the update operation
 */
export const updateInquiryStatus = async (inquiryId, newStatus, type) => {
  try {
    // Different collections based on type
    const collectionName = type === 'booking' ? 'Enquiries' : 'enquiries';
    
    // For both types, we need to find which document contains this inquiry
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    
    let updated = false;
    
    // Loop through documents to find the one containing our inquiry
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data();
      
      // Check if this document contains our inquiry
      const entryKey = `entries.${inquiryId}`;
      if (Object.keys(data).some(key => key === entryKey)) {
        // Get current entry data
        const currentEntryData = data[entryKey];
        
        // Create updated entry with the new status
        const updatedEntry = {
          ...currentEntryData,
          status: newStatus
        };
        
        // Update the entire entry with the status included
        await setDoc(
          doc(db, collectionName, docSnapshot.id), 
          { [entryKey]: updatedEntry }, 
          { merge: true }
        );
        
        updated = true;
        break;
      }
    }
    
    if (!updated) {
      throw new Error(`Inquiry ${inquiryId} not found in ${collectionName} collection`);
    }
    
    return {
      success: true,
      message: 'Status updated successfully',
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: 'Failed to update inquiry status',
    };
  }
};

/**
 * Deletes an inquiry from Firestore
 * @param {string} inquiryId - The ID of the inquiry to delete
 * @param {string} type - The type of inquiry ('booking' or general inquiry)
 * @returns {Promise} - Promise representing the delete operation
 */
export const deleteInquiry = async (inquiryId, type) => {
  try {
    // Different collections based on type
    const collectionName = type === 'booking' ? 'Enquiries' : 'enquiries';
    
    // For both types, we need to find which document contains this inquiry
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    
    let deleted = false;
    
    // Loop through documents to find the one containing our inquiry
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data();
      
      // Check if this document contains our inquiry
      const entryKey = `entries.${inquiryId}`;
      if (Object.keys(data).some(key => key === entryKey)) {
        // Use deleteField() to completely remove the field from the document
        const updateData = { [entryKey]: deleteField() };
        
        // Update the document, properly removing the entry
        await setDoc(
          doc(db, collectionName, docSnapshot.id), 
          updateData, 
          { merge: true }
        );
        
        deleted = true;
        break;
      }
    }
    
    if (!deleted) {
      throw new Error(`Inquiry ${inquiryId} not found in ${collectionName} collection`);
    }
    
    return {
      success: true,
      message: 'Inquiry deleted successfully',
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      message: 'Failed to delete inquiry',
    };
  }
};
